<script lang="ts" setup>
import { ref } from 'vue'
import type { ChatMessageSendingJobForm_MessageImage } from '../types'
import IconClose from '@/components/icons/IconClose.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'

const model = defineModel<ChatMessageSendingJobForm_MessageImage>({
  required: true,
})

const handleFileInput = async (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files?.[0] == undefined) {
    return
  }

  const file = target.files[0]

  // input の持つファイルをクリアしている
  // これをしないと以下の場合にこまる
  //   - A ファイルを選択する
  //   - dolphin 上からファイルを削除する
  //   - A ファイルを選択する
  // このとき、A ファイルが選択されたイベントが発火しない
  // なぜなら削除は Vue の状態の上での削除であり、input 要素で保持しているから
  // よって、処理をシンプルにするために、input 要素のファイルを毎回クリアする
  target.value = ''

  const img = await buildImage(file)
  if (img.naturalWidth < 1040) {
    window.alert('画像の幅が 1040px 以上である必要があります')
    return
  }

  imageSrc.value = img.src
  model.value.image = file
}

const imageSrc = ref<string>()

const buildImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      resolve(img)
    }

    img.onerror = error => {
      reject(error)
    }

    img.src = URL.createObjectURL(file)
  })
}

const handleSelectedImageRemove = () => {
  imageSrc.value = undefined
  model.value.image = undefined
}
</script>

<template>
  <section>
    <div :class="$style.imageInput">
      <label v-if="model.image == undefined" :class="$style.inputLabel"
        >画像をアップロードする
        <input
          type="file"
          accept="image/jpeg,image/png"
          @change="handleFileInput"
          :class="$style.input"
        />
      </label>
      <div v-if="imageSrc" :class="$style.selectedImageWrapper">
        <div>
          <img :class="$style.img" :src="imageSrc" alt="" />
        </div>
        <div>
          <IconClose
            :class="$style.removeButton"
            @click="handleSelectedImageRemove"
          />
        </div>
      </div>
      <div :class="$style.note">
        画像の幅は1040px以上にしてください。<br /><br />
        アップロードされた画像は第三者にも公開される URL
        からアクセスできます。<br />そのためアップロードする画像に個人情報を含めないでください。
      </div>
    </div>
    <div :class="$style.inputWrapper">
      <p :class="$style.textInputLabel">リンクURL</p>
      <DolphinTextInput v-model="model.linkUri" :class="$style.textInput" />
    </div>
    <div :class="$style.inputWrapper">
      <p :class="$style.textInputLabel">代替テキスト</p>
      <p :class="$style.textInputLabelNote">
        ユーザーがメッセージを受信した際に、端末の通知やトークリストで画像の代替として表示されます。
      </p>
      <DolphinTextInput v-model="model.altText" :class="$style.textInput" />
    </div>
  </section>
</template>

<style lang="scss" module>
.imageInput {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px;
  border: 1px solid $color-border-low-emphasis;
}

.inputLabel {
  text-decoration: underline;
  cursor: pointer;
}

.input {
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
}

.selectedImageWrapper {
  display: flex;
}

.img {
  width: 100%;
}

.removeButton {
  width: 24px;
  height: 24px;
  cursor: pointer;
  margin-left: 8px;
}

.note {
  margin-top: 16px;
}

.inputWrapper {
  margin-top: 16px;
}

.textInputLabel {
  font-size: 14px;
  font-weight: bold;
}

.textInputLabelNote {
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;
}

.textInput {
  width: 100%;
  margin-top: 4px;
}
</style>
